import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notificationsApi, type NotificationCategory } from '@/api/notificationsApi'

// Shared prefix — the bell, sidebar badge, popover, and page all key off this,
// so invalidating it (on a mutation or a WebSocket event) keeps them in sync.
export const notificationKeys = ['notifications'] as const

/** Unread count — drives the TopBar bell badge and the sidebar badge. */
export function useUnreadCount() {
  return useQuery({
    queryKey: [...notificationKeys, 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
  })
}

/** Notification list (newest-first, optionally unread-only). */
export function useNotificationList(params: { unreadOnly?: boolean } = {}) {
  const unreadOnly = params.unreadOnly ?? false
  return useQuery({
    queryKey: [...notificationKeys, 'list', { unreadOnly }],
    queryFn: () => notificationsApi.list({ unreadOnly }),
  })
}

/**
 * The Notifications page's list: newest first, one category group or all, with
 * "Load more" following the backend cursor. Keyed apart from the popover's
 * 'list' query because the cached shape differs (pages, not one page).
 */
export function useNotificationPages(
  params: { unreadOnly?: boolean; categories?: readonly NotificationCategory[] } = {},
) {
  const unreadOnly = params.unreadOnly ?? false
  const categories = params.categories ?? []
  return useInfiniteQuery({
    queryKey: [...notificationKeys, 'pages', { unreadOnly, categories }],
    queryFn: ({ pageParam }) =>
      notificationsApi.list({ unreadOnly, categories, cursor: pageParam ?? undefined }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  })
}

/** Mark one notification read; refreshes every notification consumer. */
export function useMarkRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys })
    },
  })
}

/** Mark all of the caller's unread notifications read, or only those in some categories. */
export function useMarkAllRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (categories?: readonly NotificationCategory[]) => notificationsApi.markAllRead(categories),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys })
    },
  })
}
